import { inject, Injectable } from '@angular/core';
import { BizEntityTypeCategory, IBizEntity } from '@optima-stride-ai/core';
import { IObjective } from '../../objective/objective.model';
import { UtilService } from '../util.service';
import { ObjectiveStateStoreService } from '../../objective/objective-state-store/objective-state-store.service';
import { BizEntityTypeStateStoreService } from '@optima-stride-ai/optima-app-framework';

@Injectable({
  providedIn: 'root',
})
export class BizEntityParentHierarchyService {
  private readonly utilService = inject(UtilService);
  private readonly objectiveStateStore = inject(ObjectiveStateStoreService);
  private readonly typeStateStore = inject(BizEntityTypeStateStoreService);

  resolveObjectiveFromParentHierarchy(parentEntity: IBizEntity): IBizEntity | null {
    if (parentEntity.type?.category === BizEntityTypeCategory.BenzaitenObjective) {
      return parentEntity;
    }

    const parentPath = Array.isArray(parentEntity.parentPath) ? parentEntity.parentPath : [];
    for (let index = parentPath.length - 1; index >= 0; index--) {
      const ancestor = parentPath[index];
      if (ancestor?.type?.category === BizEntityTypeCategory.BenzaitenObjective) {
        return ancestor;
      }
    }

    return null;
  }

  /**
   * Resolves and fetches the parent objective from entity hierarchy.
   * Returns fully hydrated objective with duration data.
   * 
   * @param parentEntity - The parent entity to search from
   * @returns Promise resolving to hydrated objective or null
   */
  async resolveAndFetchObjectiveFromParentHierarchy(parentEntity: IBizEntity): Promise<IObjective | null> {
    // 1. Walk hierarchy to find objective reference
    const objectiveRef = this.resolveObjectiveFromParentHierarchy(parentEntity);
    
    if (!objectiveRef?.id) {
      return null;
    }
    
    // 2. Fetch full objective data with duration
    try {
      const objectives = await this.objectiveStateStore.getState(
        this.typeStateStore,
        [objectiveRef.id.toString()]
      );
      
      return objectives?.[0] || null;
    } catch (error) {
      // Silently handle errors and return null
      return null;
    }
  }

  getDurationDisplayValue(objective: IObjective | null | undefined): { from: string; to: string; value: string } {
    const fromDate = objective?.duration?.from ?? undefined;
    const toDate = objective?.duration?.to ?? undefined;
    const from = fromDate ? this.utilService.convertDateToString(fromDate) : '';
    const to = toDate ? this.utilService.convertDateToString(toDate) : '';

    return {
      from,
      to,
      value: fromDate && toDate ? `${from} - ${to}` : '',
    };
  }
}